import useImageLoader from '@/common/hooks/useImage';
import MediaImageLoader from '@/common/components/mediaImage/MediaImageLoader';
import { PORTRAIT_GENERATION_SECONDS } from '@/common/constants/generationEstimates';
import useGenerationCountdown from '@/common/hooks/useGenerationCountdown';
import { formatTranslation } from '@/common/utils/formatTranslation';
import { formatStudioOptionValue } from '../utils/studioOptionLabel';
import { getStudioGridOptionLabel } from '../constants/studioGridOptionLabels';
import StudioGenerationLoader from './StudioGenerationLoader';
import {
	ResultAttribute,
	ResultAwakenButton,
	ResultDetails,
	ResultGrid,
	ResultIdentity,
	ResultLoadingOverlay,
	ResultPortrait,
	ResultRerollButton,
	ResultShell,
	ResultStageSection,
} from '../studio.styles';

const WIDE_FIELD_KEYS = new Set(['hairstyle', 'haircolor', 'occupation', 'kinks']);

const buildResultItems = ({ gender, age, selections, finalDetails, labels, lang, quizOptions }) => {
	const intimateLabel =
		gender === 'men' ? labels.fieldBulgeSize : labels.fieldBreastSize;
	const gridLabel = (value) => getStudioGridOptionLabel(lang, value);

	return [
		{ key: 'ethnicity', label: labels.fieldEthnicity, value: gridLabel(selections.ethnicity) },
		{ key: 'age', label: labels.fieldAge, value: `${age} ${labels.yearsOld}` },
		{ key: 'eyecolor', label: labels.fieldEyeColor, value: gridLabel(selections.eyecolor) },
		{ key: 'bodytype', label: labels.fieldBodyType, value: gridLabel(selections.bodytype) },
		{
			key: 'hairstyle',
			label: labels.fieldHairstyle,
			value: gridLabel(selections.hairstyle),
		},
		{ key: 'intimatesize', label: intimateLabel, value: gridLabel(selections.intimatesize) },
		{ key: 'haircolor', label: labels.fieldHairColor, value: gridLabel(selections.haircolor) },
		{
			key: 'personality',
			label: labels.personality,
			value: formatStudioOptionValue(lang, 'personality', finalDetails.personality, quizOptions),
		},
		{
			key: 'occupation',
			label: labels.occupation,
			value: formatStudioOptionValue(lang, 'occupation', finalDetails.occupation, quizOptions),
		},
		{
			key: 'relationship',
			label: labels.relationship,
			value: formatStudioOptionValue(lang, 'relationship', finalDetails.relationship, quizOptions),
		},
		{
			key: 'nationality',
			label: labels.nationality,
			value: formatStudioOptionValue(lang, 'nationality', finalDetails.nationality, quizOptions),
		},
		{ key: 'city', label: labels.city, value: finalDetails.city },
		{
			key: 'zodiac',
			label: labels.zodiac,
			value: formatStudioOptionValue(lang, 'zodiac', finalDetails.zodiac, quizOptions),
		},
		{
			key: 'kinks',
			label: labels.kinks,
			value: formatStudioOptionValue(lang, 'kinks', finalDetails.kinks, quizOptions),
		},
	].filter(({ value }) => Boolean(value));
};

const ResultStage = ({
	gender,
	finalDetails,
	selections,
	age,
	portraitFile,
	fallbackImage,
	onReroll,
	onAwaken,
	isGenerating = false,
	isAwakening = false,
	labels,
	lang,
	quizOptions,
}) => {
	const { imageSrc, isLoading } = useImageLoader(portraitFile?._id);
	const showLoading = isGenerating || isLoading;
	const remainingSeconds = useGenerationCountdown(
		PORTRAIT_GENERATION_SECONDS,
		showLoading,
	);
	const imageUrl = imageSrc || fallbackImage;
	const phaseLabels = [
		labels.generatingPhaseLook,
		labels.generatingPhasePersona,
		labels.generatingPhaseAwaken,
	];
	const items = buildResultItems({
		gender,
		age,
		selections,
		finalDetails,
		labels,
		lang,
		quizOptions,
	});

	return (
		<ResultStageSection data-studio-panel="result" aria-label={labels.resultTitle}>
			<ResultShell>
				<ResultPortrait>
					<MediaImageLoader
						src={imageUrl}
						alt={finalDetails.name}
						objectPosition="center top"
					/>
					{showLoading && (
						<ResultLoadingOverlay aria-hidden={!showLoading}>
							<StudioGenerationLoader
								variant="overlay"
								title={labels.generatingPhoto}
								phaseLabels={isGenerating ? phaseLabels : []}
								totalSeconds={PORTRAIT_GENERATION_SECONDS}
								remainingSeconds={remainingSeconds}
								timeLabel={
									isGenerating
										? formatTranslation(labels.estimatedPhotoTimeRemaining, {
												seconds: remainingSeconds,
											})
										: undefined
								}
							/>
						</ResultLoadingOverlay>
					)}
					<ResultIdentity>
						<strong>{finalDetails.name}</strong>
						<span>{age}</span>
					</ResultIdentity>
					<ResultRerollButton
						type="button"
						aria-label={labels.rerollLook}
						disabled={isGenerating || isAwakening}
						onClick={onReroll}
					>
						<svg viewBox="0 0 24 24" aria-hidden="true">
							<path d="M5 7.5 12 4l7 3.5v9L12 20l-7-3.5v-9Z" />
							<path d="m5 7.5 7 3.5 7-3.5M12 11v9" />
							<circle cx="9" cy="8" r=".7" />
							<circle cx="15.5" cy="8.3" r=".7" />
							<circle cx="9" cy="14.5" r=".7" />
							<circle cx="15" cy="16" r=".7" />
						</svg>
					</ResultRerollButton>
				</ResultPortrait>
				<ResultDetails>
					<ResultGrid>
						{items.map(({ key, label, value }) => {
							const isWide =
								String(value).length > 13 || WIDE_FIELD_KEYS.has(key);

							return (
								<ResultAttribute key={key} $wide={isWide}>
									<span>{label}</span>
									<strong>{value}</strong>
								</ResultAttribute>
							);
						})}
					</ResultGrid>
					<ResultAwakenButton
						type="button"
						disabled={isGenerating || isAwakening || !portraitFile?._id}
						onClick={onAwaken}
					>
						{labels.meetMySoftie}
					</ResultAwakenButton>
				</ResultDetails>
			</ResultShell>
		</ResultStageSection>
	);
};

export default ResultStage;
